import {
  type FormRow,
  type FormSection,
  type ReorderDirection,
  type WorkflowFieldPropertiesPayload,
  type WorkflowFieldType,
  workflowsApi,
} from '@/api/workflows';

export type PlaceFieldPayload = {
  type: WorkflowFieldType;
  labelEn: string;
  labelFr?: string | null;
  required?: boolean;
  properties?: WorkflowFieldPropertiesPayload;
};

export type EditFieldPayload = {
  labelEn: string;
  labelFr?: string | null;
  required?: boolean;
  properties?: WorkflowFieldPropertiesPayload;
};

export type MoveFieldPayload = {
  fromSectionId: string;
  fromRowId: string;
  fromColumnId: string;
  toSectionId: string;
  toRowId: string;
  toColumnId: string;
};

export type InsertCompoundPayload = {
  rows: {
    columns: ((Omit<PlaceFieldPayload, 'required'> & { required: boolean }) | null)[];
  }[];
};

/**
 * Everything {@link DynamicFormEditorShell} needs to mutate a canvas, abstracted away
 * from where the sections actually live. `DynamicFormBuilderPage` implements this against
 * a real `WorkflowTemplate` (one `workflowsApi` call per method, refetching the template
 * on `refresh`); a Custom Editor implements it against an in-memory `CustomElement`,
 * re-saving the whole step/section content on every mutation since there's no per-cell
 * endpoint (and no lock/autosave concern) for a standalone library item.
 *
 * Every method mutates server/local state as a side effect; the shell always follows up
 * with `refresh()` and re-renders from whatever `sections` prop the caller passes next —
 * the shell itself holds no canvas data, only UI/selection state.
 */
export type FormEditorDataSource = {
  addSection: (payload: { titleEn: string; titleFr?: string }) => Promise<FormSection>;
  editSectionTitle: (sectionId: string, titleEn: string) => Promise<void>;
  /**
   * Save a section's bilingual name from the Section Properties panel. Distinct
   * from {@link editSectionTitle} (the inline EN-only canvas rename) because the
   * panel edits both languages. Optional — only the workflow-backed data source
   * provides it; the Custom Editor has no Section Properties panel.
   */
  editSectionDetails?: (
    sectionId: string,
    payload: { titleEn: string; titleFr: string | null },
  ) => Promise<void>;
  /**
   * Toggle section repeatability + min/max instance bounds from the Section
   * Properties panel. Optional — provided only by the workflow-backed data
   * source (the repeatable endpoint requires a real workflow template).
   */
  setSectionRepeatable?: (
    sectionId: string,
    payload: { repeatable: boolean; minInstances: number; maxInstances: number | null },
  ) => Promise<void>;
  deleteSection: (sectionId: string) => Promise<void>;
  moveSection: (sectionId: string, direction: ReorderDirection) => Promise<void>;
  addRow: (sectionId: string) => Promise<FormRow>;
  deleteRow: (sectionId: string, rowId: string) => Promise<void>;
  moveRow: (sectionId: string, rowId: string, direction: ReorderDirection) => Promise<void>;
  addColumn: (sectionId: string, rowId: string) => Promise<void>;
  removeColumn: (sectionId: string, rowId: string, columnId: string) => Promise<void>;
  placeField: (
    sectionId: string,
    rowId: string,
    columnId: string,
    payload: PlaceFieldPayload,
  ) => Promise<void>;
  moveField: (payload: MoveFieldPayload) => Promise<void>;
  insertCompound: (sectionId: string, payload: InsertCompoundPayload) => Promise<void>;
  editField: (
    sectionId: string,
    rowId: string,
    columnId: string,
    payload: EditFieldPayload,
  ) => Promise<void>;
  removeField: (sectionId: string, rowId: string, columnId: string) => Promise<void>;
  /** Re-pulls the latest sections into whatever state the caller passes back as the `sections` prop. */
  refresh: () => Promise<void>;
  /**
   * Inserts a fresh, independent copy of a saved Section custom element at the end of the
   * canvas (server-side id regeneration — see `ConditionStripper`/`CustomElementPlacement`).
   * Undefined when there's no real workflow step to place into (the Custom Editor's
   * in-memory data source omits this — placing a saved section requires the server-side
   * placement endpoint, which only exists for a real `WorkflowTemplate`).
   */
  placeCustomSection?: (customElementId: string) => Promise<void>;
};

/** Builds a {@link FormEditorDataSource} backed by a real workflow step via `workflowsApi`. */
export function createWorkflowDataSource(
  templateId: string,
  stepId: string,
  refresh: () => Promise<void>,
): FormEditorDataSource {
  return {
    addSection: (payload) => workflowsApi.addFormSection(templateId, stepId, payload),
    editSectionTitle: (sectionId, titleEn) =>
      workflowsApi
        .editFormSection(templateId, stepId, sectionId, { titleEn })
        .then(() => undefined),
    editSectionDetails: (sectionId, payload) =>
      workflowsApi.editFormSection(templateId, stepId, sectionId, payload).then(() => undefined),
    setSectionRepeatable: (sectionId, payload) =>
      workflowsApi
        .setSectionRepeatable(templateId, stepId, sectionId, payload)
        .then(() => undefined),
    deleteSection: (sectionId) => workflowsApi.deleteFormSection(templateId, stepId, sectionId),
    moveSection: (sectionId, direction) =>
      workflowsApi.reorderFormSection(templateId, stepId, sectionId, direction),
    addRow: (sectionId) => workflowsApi.addFormRow(templateId, stepId, sectionId, 1),
    deleteRow: (sectionId, rowId) =>
      workflowsApi.deleteFormRow(templateId, stepId, sectionId, rowId),
    moveRow: (sectionId, rowId, direction) =>
      workflowsApi.reorderFormRow(templateId, stepId, sectionId, rowId, direction),
    addColumn: (sectionId, rowId) =>
      workflowsApi.addFormColumn(templateId, stepId, sectionId, rowId).then(() => undefined),
    removeColumn: (sectionId, rowId, columnId) =>
      workflowsApi.removeFormColumn(templateId, stepId, sectionId, rowId, columnId),
    placeField: (sectionId, rowId, columnId, payload) =>
      workflowsApi
        .placeField(templateId, stepId, sectionId, rowId, columnId, payload)
        .then(() => undefined),
    moveField: (payload) => workflowsApi.moveField(templateId, stepId, payload),
    insertCompound: (sectionId, payload) =>
      workflowsApi.insertCompound(templateId, stepId, sectionId, payload).then(() => undefined),
    editField: (sectionId, rowId, columnId, payload) =>
      workflowsApi
        .editField(templateId, stepId, sectionId, rowId, columnId, payload)
        .then(() => undefined),
    removeField: (sectionId, rowId, columnId) =>
      workflowsApi.removeField(templateId, stepId, sectionId, rowId, columnId),
    refresh,
    placeCustomSection: (customElementId) =>
      workflowsApi.placeCustomSection(templateId, stepId, customElementId).then(() => undefined),
  };
}
